import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '~/components/data-display/Icon';
import { HStack, VStack } from '~/components/layout/Stack';
import Modal from '~/components/modals/Modal/Modal';
import ModalContent from '~/components/modals/Modal/ModalContent';
import ModalTitle from '~/components/modals/Modal/ModalTitle';
import SimpleModalActions from '~/components/modals/Modal/SimpleModalActions';
import Text from '~/components/typographies/Text';
import IconArrowTrendDown from '~/icons/icon-arrow-trend-down.svg?react';
import IconArrowTrendUp from '~/icons/icon-arrow-trend-up.svg?react';
import IconLinkOn from '~/icons/icon-link-on.svg?react';
import { ModalProps } from '~/modals/Modal';
import { Counterpart } from '~/types/counterparts';
import { Document } from '~/types/document';
import { Transaction } from '~/types/transaction';
import { formatFromISOToDisplay } from '~/utils/formatDate';
import { formatMoney, isMoreThanZero } from '~/utils/money';
import styles from './BookkeepingDocumentSelectionDialog.module.scss';
import { DocumentSearch } from './DocumentSearch';

const TransactionDescription = ({ transaction }: { transaction: Transaction }) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.associate_document' });
  const { t: tTransactions } = useTranslation('transactions');
  const isPositive = isMoreThanZero(transaction.amount);

  return (
    <HStack justify="space-between" align="center" className={styles.transactionSummary}>
      <VStack gap={0.5}>
        <Text variant="caption" weight="medium" secondaryColor className={styles.transactionLabel}>
          {t('transaction')}
        </Text>
        {transaction.counterpartName && <Text variant="subtitle2">{transaction.counterpartName}</Text>}
        <Text variant="body2" weight="regular">
          {transaction.cleanDescription ?? transaction.description}
          &nbsp;•&nbsp;
          {formatFromISOToDisplay(transaction.date)}
          &nbsp;•&nbsp;
          {tTransactions('transaction_type.type', { context: transaction.transactionType })}
        </Text>
      </VStack>
      <HStack align="center" gap={1}>
        <Text variant="subtitle2">{formatMoney(transaction.amount)}</Text>
        <Icon
          component={isPositive ? IconArrowTrendUp : IconArrowTrendDown}
          size="md"
          className={isPositive ? styles.trendUp : styles.trendDown}
        />
      </HStack>
    </HStack>
  );
};

interface BookkeepingDocumentSelectionDialogProps extends ModalProps {
  transaction: Transaction | null;
  onDocumentSelect: (document: Document) => void;
  selectedCounterpart: Pick<Counterpart, 'id' | 'companyName'> | null;
}

const BookkeepingDocumentSelectionDialog = ({
  closeModal,
  transaction,
  onDocumentSelect,
  selectedCounterpart,
}: BookkeepingDocumentSelectionDialogProps) => {
  const { t } = useTranslation('bookkeeping');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  const handleDocumentSelect = (document: Document) => {
    onDocumentSelect(document);
    closeModal();
  };

  const handleRadioSelect = (document: Document) => {
    setSelectedDocument(document);
  };

  const handleConfirm = () => {
    if (selectedDocument) {
      handleDocumentSelect(selectedDocument);
    }
  };

  return (
    <Modal
      maxWidth="800px"
      fullWidth
      isOpen
      className={styles.modal}
      onOpenChange={(open) => {
        if (!open) closeModal();
      }}
    >
      <ModalTitle Icon={IconLinkOn} onCloseClick={() => closeModal()}>
        {t('transactions.associate_document.title')}
      </ModalTitle>
      <ModalContent className={styles.modalContent}>
        {transaction && <TransactionDescription transaction={transaction} />}
        <DocumentSearch
          onDocumentSelect={handleRadioSelect}
          selectedDocument={selectedDocument}
          selectedCounterpart={selectedCounterpart}
        />
        <SimpleModalActions
          confirmLabel={t('transactions.associate_document.confirm')}
          onConfirm={handleConfirm}
          onOpenChange={(isOpen) => {
            if (!isOpen) closeModal();
          }}
        />
      </ModalContent>
    </Modal>
  );
};

export default BookkeepingDocumentSelectionDialog;
